import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import SEO from "../SEO";
import { SalesCar } from "../../types/types";
import NotFound from "../NotFound";
import { salesCarsApi } from "../../lib/supabase";

const formatPrice = (price: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(price);

const formatMileage = (mileage: number) =>
  new Intl.NumberFormat("en-GB").format(mileage);

const statusLabel = (status: SalesCar["status"]) => {
  switch (status) {
    case "available":
      return "Available";
    case "reserved":
      return "Reserved";
    case "sold":
      return "Sold";
    default:
      return status;
  }
};

const SalesDetailPage: React.FC = () => {
  const { slug } = useParams();
  const [car, setCar] = useState<SalesCar | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCar = async () => {
      if (!slug) return;
      try {
        setIsLoading(true);
        setError(null);
        const data = await salesCarsApi.getBySlug(slug);
        setCar(data);
      } catch (err: any) {
        setError(`Unable to load listing: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadCar();
  }, [slug]);

  if (!isLoading && !car && !error) {
    return <NotFound />;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress sx={{ color: "#DC143C" }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!car) {
    return null;
  }

  const primaryImage = car?.images?.[car?.primary_image_index ?? 0] ?? "";

  return (
    <>
      <SEO
        title={`${car.year} ${car.make} ${car.model} | JP Performance Cars`}
        description={car.description || "Premium sales inventory listing."}
        canonical={`https://www.jpperformancecars.co.uk/sales/${car.slug}`}
      />
      <Box sx={{ bgcolor: "black", color: "white", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1}>
            <Typography variant="overline" color="rgba(255,255,255,0.7)">
              Sales Inventory
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontFamily: "Times New Roman, serif", letterSpacing: "1px" }}
            >
              {car.year} {car.make} {car.model}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={statusLabel(car.status)} color="default" />
              {car.exterior_color && <Chip label={car.exterior_color} />}
              {car.body_style && <Chip label={car.body_style} />}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                height: { xs: 280, md: 420 },
                borderRadius: 3,
                backgroundImage: primaryImage
                  ? `url(${primaryImage})`
                  : "linear-gradient(135deg, #0f172a, #1f2937)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {!primaryImage && (
                <Typography color="rgba(255,255,255,0.7)">
                  Image coming soon
                </Typography>
              )}
            </Box>
            <Stack spacing={2} sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Overview
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {car.description ||
                  "Contact our team for a full specification and provenance details."}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={3} sx={{ position: "sticky", top: 120 }}>
              <Box
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 3,
                  p: 3,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {formatPrice(car.price)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatMileage(car.mileage)} miles
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Transmission:</strong>{" "}
                    {car.transmission || "Enquire"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fuel:</strong> {car.fuel || "Enquire"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Engine:</strong> {car.engine || "Enquire"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Interior:</strong> {car.interior_color || "Enquire"}
                  </Typography>
                </Stack>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  sx={{
                    mt: 3,
                    background:
                      "linear-gradient(135deg, #DC143C 0%, #B12A37 100%)",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #B12A37 0%, #8B1538 100%)",
                    },
                  }}
                >
                  Enquire about this vehicle
                </Button>
              </Box>

              {car.features && car.features.length > 0 && (
                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 3,
                    p: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Highlights
                  </Typography>
                  <Stack spacing={1}>
                    {car.features.map((feature) => (
                      <Typography key={feature} variant="body2">
                        • {feature}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default SalesDetailPage;
